// OpportunitySignalCard.js — BL-048 (ADR-068) inline card shown with a
// completed analysis result.
//
//   • strict BUY + opportunity_persisted → an "Opportunity Signal" card that
//     states it was automatically saved (NO "Save to track" prompt) and
//     deep-links to the stable /my-analyses/:analysisId route.
//   • strict BUY but opportunity_persisted === false → show the analysis but
//     NEVER claim it was saved; offer a quiet retry.
//   • HOLD/PASS/non-BUY → render nothing.
//
// Signal-not-advice (ADR-044): carries a not-advice clarifier; no advice
// language. Reads ONLY the server-authoritative fields on the result.

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { analysisRoute } from "../lib/opportunities";
import { opportunityDisplayedWithAnalysis, opportunityOpened } from "../lib/opportunityAnalytics";

export default function OpportunitySignalCard({ result }) {
  const decision = result?.recommendation?.decision;
  // Only render on the AUTHORITATIVE path (server-persisted). On the legacy /
  // flag-off path the response carries no analysis_id and there is no
  // opportunity concept — render nothing (never a false "couldn't save").
  const authoritative = result?.analysis_id != null || result?.opportunity_persisted != null;
  const analysisId = result?.analysis_id || null;
  const persisted = result?.opportunity_persisted === true && !!result?.opportunity_id;
  const opportunityId = result?.opportunity_id || null;
  const visible = decision === "BUY" && authoritative;

  // Hooks run unconditionally (before any early return); guard inside.
  useEffect(() => {
    if (visible && persisted) opportunityDisplayedWithAnalysis({ opportunity_id: opportunityId });
  }, [visible, persisted, opportunityId]);

  if (!visible) return null;

  return (
    <section
      data-testid="opportunity-signal-card"
      className="card"
      style={{ display: "grid", gap: "var(--space-2)" }}
    >
      <h3 style={{ margin: 0 }}>Opportunity Signal</h3>
      <p data-testid="opportunity-not-advice" style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
        A signal about this analysis — not investment advice.
      </p>

      {persisted ? (
        <>
          <p data-testid="opportunity-saved" style={{ margin: 0 }}>
            Tracked ✓ — saved to Your Opportunities.
          </p>
          {analysisId && (
            <Link
              to={analysisRoute(analysisId)}
              data-testid="opportunity-open-link"
              onClick={() => opportunityOpened({ opportunity_id: opportunityId, analysis_id: analysisId })}
            >
              Open this opportunity
            </Link>
          )}
        </>
      ) : (
        <p data-testid="opportunity-not-saved" style={{ margin: 0 }}>
          We couldn't save this opportunity just now — your analysis is unaffected. Please try again.
        </p>
      )}
    </section>
  );
}
